import { forwardRef } from 'react';
import type { Ref } from 'react';

import { Divider as MuiDivider, Typography } from '@mui/material';
import { Divider as DsDivider } from '@natter-co/design-system';
import { VISUAL_REFRESH } from 'common/utils';

import { toDsProps } from './toDsProps';

import type { DividerProps as MuiDividerProps } from '@mui/material';

export type DividerProps = MuiDividerProps & {
  /** Plain text between horizontal rules. Rich children keep the legacy MUI implementation. */
  label?: string;
  /** Typed because hyphenated JSX attributes are unchecked; `toDsProps` has to see it. */
  'data-testid'?: string;
};

export const Divider = forwardRef<HTMLHRElement, DividerProps>((props, ref) => {
  // The visual-refresh gate lives here, not at the call sites. `null` falls through to MUI.
  const dsProps = VISUAL_REFRESH ? toDsProps(props) : null;

  if (!dsProps) {
    const { label, children, ...muiProps } = props;
    return (
      <MuiDivider ref={ref} {...muiProps}>
        {label ? (
          <Typography variant="natter-text-sm" color="text.secondary">
            {label}
          </Typography>
        ) : (
          children
        )}
      </MuiDivider>
    );
  }

  const { orientation, className, testId, label } = dsProps;

  if (label)
    return <DsDivider ref={ref as Ref<HTMLDivElement>} label={label} className={className} data-testid={testId} />;

  return (
    <DsDivider ref={ref as Ref<HTMLDivElement>} orientation={orientation} className={className} data-testid={testId} />
  );
});
